<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Stock Takes</h1>
          <p class="lede">Physical counts of what's actually on the shelf, reconciled against what Paysol thinks is there.</p>
        </div>
        <button class="primary" @click="showNew=true" v-if="canTake && !currentTake">+ Start new stock take</button>
      </div>

      <!-- ACTIVE COUNTING VIEW -->
      <div v-if="currentTake" class="card counting">
        <div class="counting-head">
          <div>
            <h3>Stock Take #{{ currentTake.id }} — {{ currentTake.shop_name }}</h3>
            <p class="muted small">Opened {{ currentTake.opened_at }} · {{ counted }} of {{ currentTake.items.length }} counted · variance so far: <strong :class="totalVariance === 0 ? 'ok' : (totalVariance < 0 ? 'warn' : 'muted')">{{ totalVariance }}</strong></p>
          </div>
          <div class="counting-actions">
            <button class="secondary" @click="saveCounts" :disabled="savingCounts">{{ savingCounts ? 'Saving…' : 'Save progress' }}</button>
            <button class="danger" @click="cancelTake">Cancel</button>
            <button class="primary" @click="closeTake" :disabled="closing">{{ closing ? 'Applying…' : 'Close &amp; apply variance' }}</button>
          </div>
        </div>

        <div class="filter-row">
          <input v-model="filter" placeholder="Filter items by name or SKU…" />
          <label class="inline"><input type="checkbox" v-model="showOnlyUncounted" style="width:auto; margin-right:6px" />Uncounted only</label>
          <label class="inline"><input type="checkbox" v-model="showOnlyVariance" style="width:auto; margin-right:6px" />Variance only</label>
        </div>

        <table>
          <thead>
            <tr><th>Product</th><th>Variant · SKU</th><th class="r">Expected</th><th class="r">Counted</th><th class="r">Variance</th><th>Notes</th></tr>
          </thead>
          <tbody>
            <tr v-for="it in filteredItems" :key="it.id" :class="{ counted: it.counted_qty !== null, over: (it.counted_qty || 0) > it.expected_qty, under: it.counted_qty !== null && it.counted_qty < it.expected_qty }">
              <td>{{ it.product_name }}</td>
              <td class="muted small">{{ it.variant_name }} · {{ it.sku }}</td>
              <td class="r">{{ it.expected_qty }}</td>
              <td class="r"><input type="number" v-model.number="it.counted_qty" min="0" style="width:70px; padding:4px 6px; text-align:right" /></td>
              <td class="r" :class="varianceClass(it)">
                <strong v-if="it.counted_qty !== null">{{ ((it.counted_qty || 0) - it.expected_qty) > 0 ? '+' : '' }}{{ (it.counted_qty || 0) - it.expected_qty }}</strong>
                <span v-else class="muted small">—</span>
              </td>
              <td><input v-model="it.notes" placeholder="Optional" style="width:100%; padding:4px 6px" /></td>
            </tr>
          </tbody>
        </table>

        <p v-if="closeMsg" :class="closeMsgClass">{{ closeMsg }}</p>
      </div>

      <!-- LIST OF PAST TAKES -->
      <div v-else class="card">
        <table v-if="takes.length">
          <thead><tr><th>#</th><th>Shop</th><th>Status</th><th class="r">Counted</th><th class="r">Total variance</th><th>Opened</th><th>Closed</th><th></th></tr></thead>
          <tbody>
            <tr v-for="t in takes" :key="t.id">
              <td><strong>#{{ t.id }}</strong></td>
              <td>{{ t.shop_name }}</td>
              <td><span :class="'st-pill ' + t.status">{{ t.status }}</span></td>
              <td class="r">{{ t.counted_count }} / {{ t.item_count }}</td>
              <td class="r" :class="Number(t.total_variance) < 0 ? 'warn' : ''">{{ t.total_variance || 0 }}</td>
              <td class="muted small">{{ t.opened_at }}</td>
              <td class="muted small">{{ t.closed_at || '—' }}</td>
              <td>
                <button v-if="t.status === 'open'" class="primary small" @click="resumeTake(t.id)">Resume</button>
                <button v-else class="secondary small" @click="resumeTake(t.id)">View</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No stock takes yet. Start your first one to reconcile physical vs system counts.</p>
      </div>
    </div>

    <!-- NEW modal -->
    <div v-if="showNew" class="modal-back" @click.self="showNew=false">
      <div class="modal card">
        <h3>Start new stock take</h3>
        <p class="muted small">This will freeze the current stock counts as "expected", then let you enter counted quantities. On close, differences are applied as adjustments.</p>
        <label>Notes (optional)</label>
        <input v-model="newForm.notes" placeholder="e.g. Monthly Nov count" />
        <div class="modal-actions">
          <button class="secondary" @click="showNew=false">Cancel</button>
          <button class="primary" @click="openNewTake" :disabled="opening">{{ opening ? 'Opening…' : 'Start take' }}</button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute, can } from '@/api';

export default {
  name: 'StockTakes',
  components: { Navigation },
  data() {
    return {
      takes: [],
      currentTake: null,
      showNew: false, opening: false, err: '',
      newForm: { notes: '' },
      filter: '', showOnlyUncounted: false, showOnlyVariance: false,
      savingCounts: false, closing: false,
      closeMsg: '', closeMsgClass: 'muted small',
    };
  },
  computed: {
    canTake() { return can('stock.take') || sessionStorage.getItem('userRole') === 'owner'; },
    counted() { return this.currentTake ? this.currentTake.items.filter(i => i.counted_qty !== null).length : 0; },
    totalVariance() {
      if (!this.currentTake) return 0;
      return this.currentTake.items.reduce((s, it) => {
        if (it.counted_qty === null) return s;
        return s + ((it.counted_qty || 0) - it.expected_qty);
      }, 0);
    },
    filteredItems() {
      if (!this.currentTake) return [];
      const q = this.filter.trim().toLowerCase();
      return this.currentTake.items.filter(it => {
        if (this.showOnlyUncounted && it.counted_qty !== null) return false;
        if (this.showOnlyVariance) {
          if (it.counted_qty === null) return false;
          if ((it.counted_qty || 0) === it.expected_qty) return false;
        }
        if (!q) return true;
        return (it.product_name || '').toLowerCase().includes(q) ||
               (it.variant_name || '').toLowerCase().includes(q) ||
               (it.sku || '').toLowerCase().includes(q);
      });
    },
  },
  async mounted() { await this.loadList(); },
  methods: {
    varianceClass(it) {
      if (it.counted_qty === null) return 'muted';
      const v = (it.counted_qty || 0) - it.expected_qty;
      if (v === 0) return 'ok';
      if (v < 0)   return 'warn';
      return '';
    },
    async loadList() {
      const { data } = await execute({ function: 'listStockTakes' }, paysol);
      if (data.success) this.takes = data.data;
    },
    async openNewTake() {
      this.opening = true; this.err = '';
      try {
        const { data } = await execute({ function: 'openStockTake', notes: this.newForm.notes }, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.showNew = false; this.newForm.notes = '';
        await this.resumeTake(data.data.id);
      } catch (e) { this.err = e.response?.data?.message || 'Failed.'; }
      finally { this.opening = false; }
    },
    async resumeTake(id) {
      const { data } = await execute({ function: 'getStockTake', id }, paysol);
      if (data.success) {
        this.currentTake = data.data;
        // normalise counted_qty to null when empty for cleaner display
        this.currentTake.items.forEach(it => {
          if (it.counted_qty === '' || it.counted_qty === undefined) it.counted_qty = null;
          else if (it.counted_qty !== null) it.counted_qty = Number(it.counted_qty);
        });
      }
    },
    async saveCounts() {
      this.savingCounts = true;
      try {
        const counts = this.currentTake.items
          .filter(it => it.counted_qty !== null && it.counted_qty !== '')
          .map(it => ({ takeItemId: it.id, countedQty: Number(it.counted_qty), notes: it.notes || '' }));
        const { data } = await execute({ function: 'saveStockCount', takeId: this.currentTake.id, counts }, paysol);
        if (!data.success) alert(data.message);
      } finally { this.savingCounts = false; }
    },
    async closeTake() {
      if (!confirm('Close stock take and apply variance to stock levels?')) return;
      this.closing = true; this.closeMsg = '';
      try {
        // Save one more time first
        await this.saveCounts();
        const { data } = await execute({ function: 'closeStockTake', id: this.currentTake.id }, paysol);
        if (!data.success) { this.closeMsg = data.message; this.closeMsgClass = 'err'; return; }
        this.closeMsg = `Stock take closed. ${data.data.adjustments_applied} adjustment(s) applied to stock levels.`;
        this.closeMsgClass = 'ok';
        setTimeout(async () => { this.currentTake = null; this.closeMsg = ''; await this.loadList(); }, 2000);
      } finally { this.closing = false; }
    },
    async cancelTake() {
      if (!confirm('Cancel stock take without applying variance?')) return;
      const { data } = await execute({ function: 'cancelStockTake', id: this.currentTake.id }, paysol);
      if (data.success) { this.currentTake = null; await this.loadList(); }
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1300px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; gap: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; max-width: 600px; }
.counting { padding: 20px; }
.counting-head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 16px; gap: 20px; flex-wrap: wrap; }
.counting-head h3 { margin: 0 0 4px; color: var(--paysol-teal); }
.counting-actions { display: flex; gap: 8px; }
.filter-row { display: flex; gap: 12px; align-items: center; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid var(--paysol-line); }
.filter-row input { flex: 1; max-width: 300px; }
.inline { display: inline-flex; align-items: center; margin: 0; font-size: 12px; color: var(--paysol-muted); text-transform: none; letter-spacing: 0; font-weight: 500; }
tr.counted td:first-child { border-left: 2px solid var(--paysol-teal); }
tr.over td { background: rgba(16,185,129,0.04); }
tr.under td { background: rgba(245,158,11,0.05); }
.r { text-align: right; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.center { text-align: center; padding: 40px 0; }
.ok { color: var(--paysol-success); }
.warn { color: var(--paysol-warning); }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
button.small { padding: 4px 10px; font-size: 11px; }
.st-pill {
  display: inline-block; padding: 3px 8px; border-radius: 3px; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.st-pill.open { background: rgba(79,195,247,0.15); color: #4FC3F7; }
.st-pill.completed { background: rgba(16,185,129,0.15); color: var(--paysol-success); }
.st-pill.cancelled { background: rgba(239,68,68,0.15); color: var(--paysol-danger); }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 460px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
</style>
